"use client"
import React, { useRef, useState, useEffect } from "react";

const faqCategories = [
  {
    category: "Account",
    icon: "👤",
    questions: [
      {
        q: "How do I log in to my account?",
        a: "Click the 'Sign In' button in the top right corner of the page. Enter your email and password. If you've forgotten your password, use the 'Forgot Password' link to reset it.",
      },
      {
        q: "How do I create an account?",
        a: "Click 'Sign Up' in the navigation menu. Fill in your email, create a secure password, and verify your email address. Once verified, you can start shopping immediately.",
      },
      {
        q: "How do I add balance to my account?",
        a: "Navigate to Account > Balance from your dashboard. Select your desired amount and payment method. We support cryptocurrency, credit cards, and other payment options for your convenience.",
      },
    ],
  },
  {
    category: "Crypto Payments",
    icon: "₿",
    questions: [
      {
        q: "What cryptocurrencies do you accept?",
        a: "We accept Bitcoin (BTC), Ethereum (ETH), Litecoin (LTC), and various stablecoins including USDT and USDC across multiple networks. Check the payment page for the full list of supported currencies.",
      },
      {
        q: "How long do crypto payments take?",
        a: "Cryptocurrency payments are typically confirmed within 10-30 minutes depending on network congestion. Your order will be processed automatically once the payment is confirmed on the blockchain.",
      },
      {
        q: "Is it safe to pay with crypto?",
        a: "Yes, absolutely. Cryptocurrency payments are processed through secure, encrypted channels. We use industry-leading payment processors to ensure your transactions are safe and private.",
      },
      {
        q: "What if my crypto payment doesn't go through?",
        a: "If your payment fails or times out, the funds will be returned to your wallet automatically. Contact our support team with your transaction ID if you need assistance tracking your payment.",
      },
    ],
  },
  {
    category: "Orders",
    icon: "📦",
    questions: [
      {
        q: "Where can I find my order history?",
        a: "Log in to your account and navigate to Account > Orders. You'll see a complete list of all your past orders with their status, items purchased, and order details.",
      },
      {
        q: "I have a pending order. What should I do?",
        a: "Pending orders are awaiting payment confirmation. If you've already paid, please wait 10-30 minutes for the payment to process. If the issue persists, contact support with your order ID.",
      },
    ],
  },
  {
    category: "Products",
    icon: "🛍️",
    questions: [
      {
        q: "How do I view available product stock?",
        a: "Product stock availability is displayed on each product page. You can see the current stock quantity and minimum purchase requirements before adding items to your cart.",
      },
      {
        q: "How do I purchase a product?",
        a: "Browse our products, select the item you want, choose your quantity, and click 'Add to Cart'. When ready, proceed to checkout, select your payment method, and complete your purchase.",
      },
      {
        q: "What happens after I complete my purchase?",
        a: "After successful payment, your digital products are delivered instantly to your order page. You can access them anytime from Account > Orders. Physical items will be shipped to your provided address.",
      },
    ],
  },
  {
    category: "General",
    icon: "ℹ️",
    questions: [
      {
        q: "How do I contact support?",
        a: "You can reach our support team 24/7 through our telegram bot. We typically respond within a few hours.",
      },
    ],
  },
];

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      className={`h-5 w-5 transition-transform duration-300 ${open ? "rotate-180" : "rotate-0"}`}
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.17l3.71-2.94a.75.75 0 1 1 .94 1.16l-4.2 3.33a.75.75 0 0 1-.94 0l-4.2-3.33a.75.75 0 0 1 .02-1.18z" />
    </svg>
  );
}

function AccordionItem({ q, a, index }: { q: string; a: string; index: number }) {
  const [open, setOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState("0px");

  useEffect(() => {
    if (open && contentRef.current) {
      setHeight(`${contentRef.current.scrollHeight}px`);
    } else {
      setHeight("0px");
    }
  }, [open]);

  return (
    <div className="group rounded-xl border border-orange-600/25 bg-[rgba(249,115,22,0.1)] hover:border-orange-600/50 transition-all duration-300">
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-5 py-4 text-left flex items-center justify-between gap-4"
        aria-expanded={open}
      >
        <span className="font-semibold text-white">
          {q}
        </span>
        <span className="shrink-0 text-orange-400">
          <Chevron open={open} />
        </span>
      </button>

      <div
        ref={contentRef}
        style={{ maxHeight: height }}
        className="overflow-hidden transition-[max-height] duration-400 ease-in-out"
      >
        <div className="px-5 pb-5 pt-0 leading-relaxed text-gray-400 border-t border-gray-800">
          <div className="pt-4">{a}</div>
        </div>
      </div>
    </div>
  );
}

export default function FAQPage() {
  return (
    <main className="min-h-screen py-35">
      <div className="container mx-auto max-w-4xl px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold mb-3 text-white">
            Frequently Asked <span className="bg-gradient-to-r from-orange-600 via-orange-500 to-orange-400 bg-clip-text text-transparent">Questions</span>
          </h1>
          <p className="text-gray-400">
            Find answers to common questions about our platform
          </p>
        </div>

        {/* Support Banner */}
        <div className="mb-10 rounded-xl border border-orange-600/25 bg-gradient-to-r from-orange-600/10 to-transparent p-6">
          <div className="flex items-start gap-4">
            <svg className="w-6 h-6 text-orange-400 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="font-semibold text-white mb-1">Need More Help?</h3>
              <p className="text-sm text-gray-400">
                Can't find what you're looking for? Our support team is available 24/7 via telegram.
              </p>
            </div>
          </div>
        </div>

        {/* FAQ Categories */}
        <div className="space-y-8">
          {faqCategories.map((category, catIndex) => (
            <div key={catIndex}>
              <div className="flex items-center gap-3 mb-4">
                <span className="text-2xl">{category.icon}</span>
                <h2 className="text-2xl font-bold text-white">{category.category}</h2>
              </div>
              <div className="space-y-3">
                {category.questions.map((item, i) => (
                  <AccordionItem 
                    key={`${catIndex}-${i}`} 
                    index={catIndex * 100 + i} 
                    q={item.q} 
                    a={item.a} 
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </main>
  );
}
